import { useCallback, useEffect, useReducer, useState } from 'react'

import * as Sentry from '@sentry/react'

import { SocialProvider } from '@reown/appkit'
import { SignClientTypes, User, WalletRequest, isMatchingUsers } from '@reown/web-wallet-core'

import { useWallet } from '../hooks/useWallet'
import { AppLogo } from './ui/AppLogo'
import { AppProviderLogos } from './ui/AppProviderLogos'
import { Container } from './ui/Container'
import { DomainVerify } from './ui/DomainVerify'
import { ErrorContent } from './ui/ErrorContent'
import { Header } from './ui/Header'
import Spacer from './ui/Spacer'
import { SuccessContent } from './ui/SuccessContent'

type PeerMetadata = SignClientTypes.Metadata

type RequestState = {
  status: 'checking' | 'processing' | 'success' | 'error'
  error?: string
  currentRequest?: WalletRequest
  expectedUser?: User
  needsUserSwitch: boolean
  peerMetadata?: PeerMetadata
}

type RequestProps = {
  requestId: number
  sessionTopic: string
}

export function Request({ requestId, sessionTopic }: RequestProps) {
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const { wallet, error: walletError } = useWallet({
    redirectUrl: window.location.origin
  })

  const [requestState, dispatch] = useReducer(
    (state: RequestState, action: Partial<RequestState>): RequestState => ({
      ...state,
      ...action
    }),
    {
      status: 'checking',
      needsUserSwitch: false
    }
  )

  const updateRequestState = useCallback((update: Partial<RequestState>) => {
    dispatch(update)
  }, [])

  // Set error from wallet initialization if it exists
  useEffect(() => {
    if (walletError) {
      updateRequestState({
        status: 'error',
        error: walletError
      })
    }
  }, [walletError, updateRequestState])

  // Handle the request once we have it
  const handleRequest = useCallback(
    async (request: WalletRequest) => {
      if (!wallet) return

      try {
        updateRequestState({
          status: 'processing',
          currentRequest: request,
          expectedUser: request.user
        })

        const sessions = wallet.walletKit?.getActiveSessions()
        const requestSession = sessions?.[request.topic]
        const peerMetadata = requestSession?.peer.metadata

        // Check if we're already connected with the right user
        if (wallet.getState() === 'READY') {
          const currentUser = await wallet.getCurrentUser()

          if (!currentUser) {
            updateRequestState({ needsUserSwitch: true, peerMetadata })
            return
          }

          if (isMatchingUsers(currentUser, request.user)) {
            await Sentry.startSpan(
              { op: 'wallet.processRequest', name: 'Process Wallet Request' },
              async span => {
                span.setAttribute('requestId', String(request.id))
                span.setAttribute('topic', request.topic)
                await wallet.processRequest(request, true)
              }
            )
            await wallet.walletKit?.core.relayer.transportClose()
            updateRequestState({ status: 'success', peerMetadata })
          } else {
            await wallet.logout()
            updateRequestState({ needsUserSwitch: true, peerMetadata })
          }
        } else {
          const walletState = wallet.getState()
          await wallet.logout()
          updateRequestState({ needsUserSwitch: true, peerMetadata })
        }
      } catch (err) {
        Sentry.captureException(err)
        updateRequestState({
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to handle request'
        })
      }
    },
    [wallet, updateRequestState]
  )

  // Subscribe to session requests and wait for our specific request
  useEffect(() => {
    if (!wallet) return

    let unsubscribe: (() => void) | undefined

    const setupSubscription = async () => {
      try {
        // First check if the request is already pending
        const pendingRequests = await wallet.getPendingRequests()
        const targetRequest = pendingRequests.find(
          req => req.id === requestId && req.topic === sessionTopic
        )

        if (targetRequest) {
          handleRequest(targetRequest)
          return
        }

        // If not found, subscribe to new requests
        unsubscribe = wallet.subscribeToSessionRequests((request: WalletRequest) => {
          if (request.id === requestId && request.topic === sessionTopic) {
            handleRequest(request)
          }
        })

        // Set a timeout to show error if request is not received
        const timeoutId = setTimeout(() => {
          updateRequestState({
            status: 'error',
            error: 'Request timeout: The specified request was not found'
          })
          if (unsubscribe) unsubscribe()
        }, 120000)

        return () => clearTimeout(timeoutId)
      } catch (err) {
        updateRequestState({
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to check for requests'
        })
      }
    }

    setupSubscription()

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [wallet, requestId, sessionTopic, handleRequest, updateRequestState])

  const handleConnect = async () => {
    if (isLoggingIn) return

    if (!wallet || !requestState.expectedUser) {
      updateRequestState({
        status: 'error',
        error: 'Wallet not initialized or no pending requests'
      })
      return
    }

    setIsLoggingIn(true)

    try {
      await wallet.login(requestState.expectedUser.loginProvider)

      const currentUser = await wallet.getCurrentUser()

      if (!currentUser) {
        throw new Error('No user logged in')
      }

      if (!requestState.expectedUser) {
        throw new Error('No expected user found')
      }

      if (!isMatchingUsers(currentUser, requestState.expectedUser)) {
        throw new Error('Wrong user logged in. Please use the correct account', {
          cause: {
            currentUser,
            expectedUser: requestState.expectedUser
          }
        })
      }

      updateRequestState({ needsUserSwitch: false })
      if (requestState.currentRequest) {
        await handleRequest(requestState.currentRequest)
      }
    } catch (err) {
      Sentry.captureException(err)
      updateRequestState({
        status: 'error',
        error: err instanceof Error ? err.message : 'Failed to login'
      })
    } finally {
      setIsLoggingIn(false)
    }
  }

  const UserSwitchContent = () => (
    <>
      <Header text="Connect to Provider" />
      <Spacer size="2" />
      {requestState.expectedUser?.loginProvider && (
        <AppProviderLogos
          appIcon={requestState.peerMetadata?.icons?.[0] || '/reown-logo.webp'}
          provider={requestState.expectedUser.loginProvider as SocialProvider}
        />
      )}
      <Spacer size="4" />
      {requestState.currentRequest?.verifyContext && (
        <DomainVerify
          verifyContext={requestState.currentRequest?.verifyContext}
          peerMetadata={requestState.peerMetadata}
        />
      )}
      <Spacer size="2" />
      <wui-text variant="md-regular" color="secondary" align="center">
        Please connect with the account you associated with this session.
      </wui-text>
      <Spacer size="2" />
      <wui-button
        size="lg"
        fullWidth
        variant="accent-primary"
        onClick={handleConnect}
        loading={isLoggingIn || undefined}
      >
        <wui-flex columnGap="1">
          Proceed
          <wui-icon size="sm" color="accent-primary" name="chevronRight" />
        </wui-flex>
      </wui-button>
    </>
  )

  const LoadingContent = () => (
    <>
      <AppLogo />
      <Spacer size="2" />
      <wui-text variant="lg-medium" color="primary" align="center">
        {requestState.status === 'processing' ? 'Processing request...' : 'Checking for request...'}
      </wui-text>
      <Spacer size="2" />
      <wui-button size="lg" fullWidth variant="accent-primary" loading />
    </>
  )

  const SuccessFooter = () => (
    <wui-text variant="md-regular" color="secondary" align="center">
      You can now return to the app.
    </wui-text>
  )

  const renderContent = () => {
    if (requestState.error) {
      return <ErrorContent title={requestState.error} peerMetadata={requestState.peerMetadata} />
    }

    if (requestState.needsUserSwitch && requestState.expectedUser) {
      return <UserSwitchContent />
    }

    if (requestState.status === 'success') {
      return (
        <SuccessContent
          title="Request processed successfully!"
          peerMetadata={requestState.peerMetadata}
        />
      )
    }

    return <LoadingContent />
  }

  const renderFooter = () => {
    if (requestState.status === 'success') {
      return <SuccessFooter />
    }

    return null
  }

  return (
    <>
      <Container>{renderContent()}</Container>
      <Spacer size="2" />
      {renderFooter()}
    </>
  )
}
